import React, { useEffect, useMemo, useState } from "react";
import { collection, addDoc, getDocs, doc, getDoc, writeBatch } from "firebase/firestore";
import { db } from "../../config/firebase";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";

const COLLECTION = "azbga_seniors";

const SEED_SENIORS = [
  {
    gym: "Aspire",
    athleteName: "Dylan Briones",
    athleteLevel: 10,
    parentName: "Laurie Briones",
    parentEmail: "ldbn1010@hotmail.com"
  },
  {
    gym: "Aspire",
    athleteName: "Asher Nelson",
    athleteLevel: 10,
    parentName: "Trine Nelson",
    parentEmail: "aaronandtrine@gmail.com"
  },
  {
    gym: "Aspire",
    athleteName: "Carlos Lopez",
    athleteLevel: 10,
    parentName: "Jenny Lopez",
    parentEmail: "jenn716@hotmail.com"
  },
  {
    gym: "Aspire",
    athleteName: "Santiago Sedillo",
    athleteLevel: 7,
    parentName: "Irene Sedillo",
    parentEmail: "irenesed@hotmail.com"
  },
  {
    gym: "USA YOUTH FITNESS CENTER",
    athleteName: "Chris Stillwell",
    athleteLevel: 10,
    parentName: "Karen Stillwell",
    parentEmail: "karen_stillwell@hotmail.com"
  },
  {
    gym: "USA YOUTH FITNESS CENTER",
    athleteName: "Oscar Salazar",
    athleteLevel: 10,
    parentName: "Iris Salazar",
    parentEmail: "irism7117@cox.net"
  },
  {
    gym: "USA YOUTH FITNESS CENTER",
    athleteName: "Jake Abelson",
    athleteLevel: 10,
    parentName: "Mariana Abelson",
    parentEmail: "marianaski@gmail.com"
  },
  {
    gym: "North Valley Gymnastics",
    athleteName: "Xavier Neumann",
    athleteLevel: 10,
    parentName: "Erin Neumann",
    parentEmail: "ffwife32601@cox.net"
  },
  {
    gym: "North Valley Gymnastics",
    athleteName: "Brooks Link",
    athleteLevel: 10,
    parentName: "Kelsey Link",
    parentEmail: "Kelseylink09@gmail.com"
  }
];

const Seniors = () => {
  const { user: currentUser } = useAuth();
  const [userRole, setUserRole] = useState(null);
  const [seniors, setSeniors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [formData, setFormData] = useState({
    gym: "",
    athleteName: "",
    athleteLevel: "",
    parentName: "",
    parentEmail: ""
  });

  const sortedSeniors = useMemo(() => {
    return [...seniors].sort((a, b) => {
      const gymCompare = (a.gym || "").localeCompare(b.gym || "");
      if (gymCompare !== 0) return gymCompare;
      return (a.athleteName || "").localeCompare(b.athleteName || "");
    });
  }, [seniors]);

  const fetchSeniors = async () => {
    setLoading(true);
    try {
      const snapshot = await getDocs(collection(db, COLLECTION));
      const list = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));
      setSeniors(list);
    } catch (err) {
      console.error("Error loading seniors:", err);
      setError("Failed to load seniors.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSeniors();
  }, []);

  useEffect(() => {
    const fetchRole = async () => {
      if (!currentUser) return;
      try {
        const userDoc = await getDoc(doc(db, "users", currentUser.uid));
        if (userDoc.exists()) {
          setUserRole(userDoc.data().role || null);
        }
      } catch (error) {
        console.error("Error fetching user role:", error);
      }
    };

    fetchRole();
  }, [currentUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!formData.gym.trim() || !formData.athleteName.trim() || !formData.athleteLevel || !formData.parentName.trim() || !formData.parentEmail.trim()) {
      setError("All fields are required.");
      return;
    }

    try {
      setSubmitting(true);
      await addDoc(collection(db, COLLECTION), {
        ...formData,
        athleteLevel: Number(formData.athleteLevel),
        createdAt: new Date()
      });
      setSuccess("Senior added.");
      setFormData({
        gym: "",
        athleteName: "",
        athleteLevel: "",
        parentName: "",
        parentEmail: ""
      });
      fetchSeniors();
    } catch (err) {
      console.error("Error adding senior:", err);
      setError("Failed to add senior.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSeed = async () => {
    setError("");
    setSuccess("");
    try {
      setSeeding(true);
      const batch = writeBatch(db);
      SEED_SENIORS.forEach((senior) => {
        const docRef = doc(collection(db, COLLECTION));
        batch.set(docRef, {
          ...senior,
          createdAt: new Date()
        });
      });
      await batch.commit();
      setSuccess("Seeded seniors list.");
      fetchSeniors();
    } catch (err) {
      console.error("Error seeding seniors:", err);
      setError("Failed to seed seniors.");
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Seniors List</h1>
            <p className="mt-2 text-gray-600 dark:text-gray-300">
              Coaches, gym admins, and admins can list senior athletes and parent contact info.
            </p>
          </div>
          <Link
            to="/admin"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-center"
          >
            Back to Dashboard
          </Link>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Add Senior</h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Gym *</label>
              <input
                type="text"
                name="gym"
                value={formData.gym}
                onChange={handleChange}
                className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2"
                placeholder="Gym name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Athlete Name *</label>
              <input
                type="text"
                name="athleteName"
                value={formData.athleteName}
                onChange={handleChange}
                className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2"
                placeholder="Athlete name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Athlete Level *</label>
              <select
                name="athleteLevel"
                value={formData.athleteLevel}
                onChange={handleChange}
                className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2"
              >
                <option value="">Select level</option>
                {[4, 5, 6, 7, 8, 9, 10].map((lvl) => (
                  <option key={lvl} value={lvl}>{lvl}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Parent Name *</label>
              <input
                type="text"
                name="parentName"
                value={formData.parentName}
                onChange={handleChange}
                className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2"
                placeholder="Parent name"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Parent Email Address *</label>
              <input
                type="email"
                name="parentEmail"
                value={formData.parentEmail}
                onChange={handleChange}
                className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2"
                placeholder="Parent email"
              />
            </div>
            <div className="md:col-span-2 flex flex-col sm:flex-row gap-3">
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60"
              >
                {submitting ? "Adding..." : "Add Senior"}
              </button>
            </div>
          </form>
          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}
          {success && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
              {success}
            </div>
          )}
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Current Seniors</h2>
            {userRole === "admin" && (
              <button
                onClick={handleSeed}
                disabled={seeding}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 disabled:opacity-60"
              >
                {seeding ? "Seeding..." : "Import provided list"}
              </button>
            )}
          </div>
          {userRole !== "admin" ? (
            <p className="text-gray-600 dark:text-gray-300">
              Submissions are saved. Only admins can view the full seniors list.
            </p>
          ) : loading ? (
            <p className="text-gray-600 dark:text-gray-300">Loading seniors...</p>
          ) : sortedSeniors.length === 0 ? (
            <p className="text-gray-600 dark:text-gray-300">No seniors listed yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-700">
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">Gym</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">Athlete Name</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-700 dark:text-gray-200">Level</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">Parent Name</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">Parent Email</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {sortedSeniors.map((senior) => (
                    <tr key={senior.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-4 py-3 text-gray-900 dark:text-gray-100">{senior.gym}</td>
                      <td className="px-4 py-3 text-gray-900 dark:text-gray-100">{senior.athleteName}</td>
                      <td className="px-4 py-3 text-center text-gray-900 dark:text-gray-100">{senior.athleteLevel}</td>
                      <td className="px-4 py-3 text-gray-900 dark:text-gray-100">{senior.parentName}</td>
                      <td className="px-4 py-3 text-gray-900 dark:text-gray-100">{senior.parentEmail}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Seniors;
